import { useState, useEffect, useMemo, useRef } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { postShowData } from '../../../util/util';
import { compressImage, removeShowImages, uploadShowImage } from '../../../util/show-images';
import Modal from './Modal';
import ModalActions from './ModalActions';
import SortableImageTile from '../SortableImageTile';
import { useToast } from '../../ui/Toast';

const MAX_FILE_SIZE_BYTES = 150 * 1024 * 1024;
const MAX_FILE_SIZE_LABEL = '150MB';

function ManageShowImagesModal({
  isOpen,
  setIsOpen,
  selectedItem,
  currentImageUrls,
  onImagesSaved,
  pendingEntries,
  onPendingSaved,
}) {
  const { showSuccess, showError } = useToast();
  // modal holds the images until the show is added (for new shows)
  const isDeferred = !selectedItem?.show_id;

  const [images, setImages] = useState([]);
  const [removedUrls, setRemovedUrls] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [oversizedFiles, setOversizedFiles] = useState([]);
  const sessionCreatedRef = useRef(new Set());
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (isOpen) {
      setImages(
        isDeferred
          ? (pendingEntries ?? []).map(({ file, previewUrl }) => ({ id: previewUrl, kind: 'new', file, previewUrl }))
          : (currentImageUrls ?? []).map(url => ({ id: url, kind: 'existing', url }))
      );
      setRemovedUrls([]);
      setOversizedFiles([]);
      sessionCreatedRef.current = new Set();
    }
  }, [isOpen, currentImageUrls, pendingEntries, isDeferred]);

  const onDrop = async (acceptedFiles, fileRejections) => {
    setOversizedFiles(
      fileRejections
        .filter(({ errors }) => errors.some(e => e.code === 'file-too-large'))
        .map(({ file }) => file.name)
    );
    if (acceptedFiles.length === 0) return;

    setIsCompressing(true);
    setCompressionProgress(0);
    const progressByFile = new Array(acceptedFiles.length).fill(0);
    const reportProgress = (index, percent) => {
      progressByFile[index] = percent;
      const average = progressByFile.reduce((sum, p) => sum + p, 0) / progressByFile.length;
      setCompressionProgress(Math.round(average));
    };
    try {
      const compressedFiles = await Promise.all(
        acceptedFiles.map((file, i) => compressImage(file, percent => reportProgress(i, percent)))
      );
      const newItems = compressedFiles.map(file => {
        const previewUrl = URL.createObjectURL(file);
        if (isDeferred) sessionCreatedRef.current.add(previewUrl);
        return { id: previewUrl, kind: 'new', file, previewUrl };
      });
      setImages(prev => [...prev, ...newItems]);
    } finally {
      setIsCompressing(false);
      setCompressionProgress(0);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: true,
    disabled: isSaving || isCompressing,
    maxSize: MAX_FILE_SIZE_BYTES,
  });

  const handleRemove = id => {
    const item = images.find(img => img.id === id);
    if (!item) return;
    if (item.kind === 'existing') {
      setRemovedUrls(prev => [...prev, item.url]);
    } else if (isDeferred) {
      if (sessionCreatedRef.current.has(item.previewUrl)) {
        URL.revokeObjectURL(item.previewUrl);
        sessionCreatedRef.current.delete(item.previewUrl);
      }
    } else {
      URL.revokeObjectURL(item.previewUrl);
    }
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setImages(prev => {
      const oldIndex = prev.findIndex(img => img.id === active.id);
      const newIndex = prev.findIndex(img => img.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const newImages = images.filter(img => img.kind === 'new');
  const revokeNewPreviews = () => newImages.forEach(img => URL.revokeObjectURL(img.previewUrl));

  const handleClose = () => {
    if (isSaving || isCompressing) return;
    if (isDeferred) {
      // to cancel
      sessionCreatedRef.current.forEach(url => URL.revokeObjectURL(url));
      sessionCreatedRef.current = new Set();
    } else {
      revokeNewPreviews();
    }
    setIsOpen(false);
  };

  const handleSave = async () => {
    if (isSaving) return;

    if (isDeferred) {
      onPendingSaved?.(images.map(({ file, previewUrl }) => ({ file, previewUrl })));
      sessionCreatedRef.current = new Set();
      setIsOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      const showId = selectedItem.show_id;

      const uploadedUrlById = {};
      await Promise.all(
        newImages.map(async img => {
          uploadedUrlById[img.id] = await uploadShowImage(showId, img.file);
        })
      );

      const finalUrls = images.map(img => (img.kind === 'existing' ? img.url : uploadedUrlById[img.id]));

      await postShowData({ id: showId, image_urls: finalUrls }, false);
      await removeShowImages(removedUrls);

      revokeNewPreviews();
      onImagesSaved?.(finalUrls);
      setIsOpen(false);
      showSuccess('Images saved.');
    } catch (error) {
      console.error('Error saving images:', error);
      showError(`Failed to save images: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (isDeferred) return true;
    const existingIdsInOrder = images.filter(img => img.kind === 'existing').map(img => img.url);
    const orderChanged =
      existingIdsInOrder.length === (currentImageUrls?.length ?? 0) &&
      existingIdsInOrder.some((url, i) => url !== currentImageUrls[i]);
    return newImages.length > 0 || removedUrls.length > 0 || orderChanged;
  }, [isDeferred, images, newImages.length, removedUrls, currentImageUrls]);

  const isBusy = isSaving || isCompressing;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Manage Images"
      description={
        images.length > 1 && (
          <p className="text-xs text-slate-400 mt-1">Drag to reorder — the first image is used as the cover.</p>
        )
      }
      maxWidth="max-w-3xl"
      zIndex="z-[60]"
    >
      {oversizedFiles.length > 0 && (
        <div className="mb-4 rounded-lg bg-secondary-red/10 border border-secondary-red/30 px-3 py-2 text-sm text-secondary-red">
          {oversizedFiles.length === 1
            ? `"${oversizedFiles[0]}" is too large to process (over ${MAX_FILE_SIZE_LABEL}) and was not added.`
            : `These files are too large to process (over ${MAX_FILE_SIZE_LABEL}) and were not added: ${oversizedFiles.join(', ')}`}
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={images.map(img => img.id)} strategy={rectSortingStrategy}>
            {images.map((img, i) => (
              <SortableImageTile
                key={img.id}
                img={img}
                isFirst={i === 0}
                disabled={isBusy}
                onRemove={handleRemove}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Drop zone tile */}
        <div
          {...getRootProps()}
          className={`h-40 w-40 rounded-lg border border-dashed border-black/20 hover:border-solid flex flex-col items-center justify-center cursor-pointer transition-colors
            ${isBusy ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <input {...getInputProps()} />
          {isCompressing ? (
            <>
              <span className="text-lg font-medium text-slate-400 mb-1">{compressionProgress}%</span>
              <span className="text-xs text-center leading-tight px-2 text-slate-400">
                Compressing…
              </span>
            </>
          ) : (
            <>
              <PlusIcon className="h-6 w-6 mb-1 text-slate-300" />
              <span className="text-xs text-center leading-tight px-2 text-slate-400">
                Click or drag
              </span>
              <span className="text-[10px] text-center leading-tight px-2 text-slate-400 mt-0.5">
                Photos are compressed automatically
              </span>
            </>
          )}
        </div>
      </div>

      <ModalActions
        onCancel={handleClose}
        onConfirm={handleSave}
        isLoading={isSaving}
        disabled={isCompressing}
        confirmDisabled={!hasChanges}
      />
    </Modal>
  );
}

export default ManageShowImagesModal;
